import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useReadOnly } from '../../contexts/WizardAccessContext';
import { useAskRobbie } from '../../contexts/RobbieUiContext';
import useCriteriaPointers from '../../hooks/useCriteriaPointers';
import { CHANGE_CRITERIA_MESSAGE, notesChip, pointerChips, structuredCriteriaChips } from '../../utils/criteriaChips';
import CriteriaBrief, { CriteriaBriefProps } from './CriteriaBrief';

/**
 * CriteriaBrief for the RFQ on screen: the saved criteria from the store,
 * Robbie's pointers from the server — or, while they are pending or
 * unavailable, the buyer's notes as written. No notes, no notes chip. "Ask Robbie to change" sends the
 * request into the wizard's conversation — editors only, since viewers
 * cannot chat — after `onAsk` lets the host step aside (a modal closes so
 * the chat is in view).
 */
export interface RfqCriteriaBriefProps
  extends Pick<CriteriaBriefProps, 'variant' | 'surface' | 'labelled' | 'className'> {
  onAsk?: () => void;
  /** Leave out the inline link where the host shows its own (`useAskRobbieToChange`). */
  hideAsk?: boolean;
}

/** "Ask Robbie to change" for the RFQ on screen — undefined where the buyer cannot chat. */
export const useAskRobbieToChange = (onAsk?: () => void): (() => void) | undefined => {
  const readOnly = useReadOnly();
  const askRobbie = useAskRobbie();
  return askRobbie && !readOnly
    ? () => {
        onAsk?.();
        askRobbie(CHANGE_CRITERIA_MESSAGE);
      }
    : undefined;
};

export const RfqCriteriaBrief: React.FC<RfqCriteriaBriefProps> = ({ onAsk, hideAsk, ...layout }) => {
  const rfqId = useSelector((state: RootState) => state.rfq.rfqData?.rfqId);
  const terms = useSelector((state: RootState) => state.rfq.commercialTerms);
  const pointers = useCriteriaPointers(rfqId, terms.additionalRequirements);
  const showNotes = pointers.phase === 'loading' || pointers.phase === 'unavailable';
  const chips = useMemo(
    () => [...structuredCriteriaChips(terms), ...(showNotes ? notesChip(terms.additionalRequirements) : [])],
    [terms, showNotes],
  );
  const noteChips = useMemo(() => pointerChips(pointers.pointers), [pointers.pointers]);

  const onAskRobbie = useAskRobbieToChange(onAsk);

  return (
    <CriteriaBrief
      chips={chips}
      noteChips={noteChips}
      onAskRobbie={hideAsk ? undefined : onAskRobbie}
      {...layout}
    />
  );
};

export default RfqCriteriaBrief;
